import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Modal from '@/components/ui/Modal';
import {
  faEnvelope,
  faSpinner,
  faArrowLeft,
  faCheckCircle,
  faExclamationTriangle,
  faClock,
  faPaperPlane,
  faCalendarAlt,
  faRefresh,
  faTimesCircle
} from '@/fontawesome';
import { handleAPIError, showSuccessToast } from '@/utils/toasts';
import { formatDate } from '@/utils/helpers';
import { APIRoutes } from '@/services/api.routes';
import type { Mail, MailStatus } from '@/models';
import type { MailID } from '@/models';
import {APIService} from "@/services/api.ts";

export default function AdminMailDetail() {
  const { mailId } = useParams<{ mailId: string }>();
  const [mail, setMail] = useState<Mail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showFailModal, setShowFailModal] = useState(false);
  const [failReason, setFailReason] = useState('');

  useEffect(() => {
    if (mailId) {
      fetchMailDetails();
    }
  }, [mailId]);

  const fetchMailDetails = async () => {
    if (!mailId) return;

    try {
      setLoading(true);
      setError(null);

      const mailResponse = await APIRoutes.getMail(mailId as MailID);
      setMail(mailResponse);
    } catch (err) {
      setError('Fehler beim Laden der E-Mail-Details');
      handleAPIError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSetToFailed = async () => {
    if (!mail || !failReason.trim()) return;

    try {
      setActionLoading(true);
      await APIRoutes.setMailToFailed(mail.id, failReason.trim());
      showSuccessToast('E-Mail wurde als fehlgeschlagen markiert');
      setShowFailModal(false);
      setFailReason('');
      await fetchMailDetails();
    } catch (err) {
      handleAPIError(err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleResetForRetry = async () => {
    if (!mail) return;

    try {
      setActionLoading(true);
      await APIRoutes.resetMailForRetry(mail.id);
      showSuccessToast('E-Mail wurde für Wiederholung zurückgesetzt');
      await fetchMailDetails();
    } catch (err) {
      handleAPIError(err);
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusIcon = (status: MailStatus) => {
    switch (status) {
      case 'WAITING':
        return <FontAwesomeIcon icon={faClock} className="w-5 h-5 text-yellow-600" />;
      case 'SENDING':
        return <FontAwesomeIcon icon={faPaperPlane} className="w-5 h-5 text-blue-600 animate-pulse" />;
      case 'SUCCESS':
        return <FontAwesomeIcon icon={faCheckCircle} className="w-5 h-5 text-green-600" />;
      case 'FAILED':
        return <FontAwesomeIcon icon={faExclamationTriangle} className="w-5 h-5 text-red-600" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: MailStatus) => {
    switch (status) {
      case 'WAITING':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-400';
      case 'SENDING':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-400';
      case 'SUCCESS':
        return 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-400';
      case 'FAILED':
        return 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/50 dark:text-gray-400';
    }
  };

  const canSetToFailed = mail?.status === 'WAITING';
  const canRetry = mail?.status === 'FAILED';

  if (loading) {
    return (
      <>
        <div className="flex justify-center items-center h-64">
          <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />
        </div>
      </>
    );
  }

  if (error || !mail) {
    return (
      <>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg">
            <p className="text-red-600 dark:text-red-400">{error || 'E-Mail nicht gefunden'}</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center">
            <Link
              to="/admin/mails"
              className="mr-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="w-5 h-5" />
            </Link>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              E-Mail Details
            </h1>
          </div>
          <div className="flex items-center space-x-2">
            {/* Action buttons can be added here */}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Header with status and actions */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <span className={`inline-flex items-center px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(mail.status)}`}>
                  {getStatusIcon(mail.status)}
                  <span className="ml-2">{mail.status}</span>
                </span>
                <span className="text-sm text-gray-500 dark:text-gray-400 font-mono">
                  ID: { mail.id}
                </span>
              </div>
              <div className="flex space-x-2">
                {canSetToFailed && (
                  <button
                    onClick={() => setShowFailModal(true)}
                    disabled={actionLoading}
                    className="bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 cursor-pointer"
                  >
                    <FontAwesomeIcon icon={faTimesCircle} className="w-4 h-4" />
                    <span>Als fehlgeschlagen markieren</span>
                  </button>
                )}
                {canRetry && (
                  <button
                    onClick={handleResetForRetry}
                    disabled={actionLoading}
                    className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 cursor-pointer"
                  >
                    <FontAwesomeIcon icon={faRefresh} className="w-4 h-4" />
                    <span>Für Wiederholung zurücksetzen</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Mail details */}
          <div className="p-6 space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Empfänger</h3>
                <div className="space-y-2">
                  <div className="flex items-center text-gray-600 dark:text-gray-400">
                    <FontAwesomeIcon icon={faEnvelope} className="w-4 h-4 mr-2" />
                    <span className="ml-2">{mail.receiverEmail}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Subject */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Betreff</h3>
              <p className="text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-900 p-3 rounded-lg">
                {mail.subject}
              </p>
            </div>

            {/* Template Info */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Template</h3>
              <p className="text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-900 p-3 rounded-lg font-mono">
                {mail.template}
              </p>
            </div>

            {/* Body Info */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Body</h3>
              <div className="max-h-50 overflow-y-auto">
                <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                  <pre className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap font-mono">
                    {mail.body}
                  </pre>
                </div>
              </div>
            </div>

            {/* Preview */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Preview</h3>
              <div className="h-180 bg-white p-4 overflow-y-auto rounded-lg">
                  <iframe className="h-full w-full" src={`${import.meta.env.VITE_API_URL}/mails/${mail.id}/body?auth=${APIService.inst.getToken()}`}></iframe>
              </div>
            </div>

            {/* Timestamps and Status Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Zeitstempel</h3>
                <div className="space-y-2">
                  <div className="flex items-center text-gray-600 dark:text-gray-400">
                    <FontAwesomeIcon icon={faCalendarAlt} className="w-4 h-4 mr-2" />
                    <span className="font-medium">Erstellt:</span>
                    <span className="ml-2">{formatDate(mail.created)}</span>
                  </div>
                  {mail.sentAt && (
                    <div className="flex items-center text-gray-600 dark:text-gray-400">
                      <FontAwesomeIcon icon={faCalendarAlt} className="w-4 h-4 mr-2" />
                      <span className="font-medium">Gesendet:</span>
                      <span className="ml-2">{formatDate(mail.sentAt)}</span>
                    </div>
                  )}
                  {mail.nextRetryAt && (
                    <div className="flex items-center text-gray-600 dark:text-gray-400">
                      <FontAwesomeIcon icon={faCalendarAlt} className="w-4 h-4 mr-2" />
                      <span className="font-medium">Nächster Versuch:</span>
                      <span className="ml-2">{formatDate(mail.nextRetryAt)}</span>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Status Info</h3>
                <div className="space-y-2">
                  <div className="flex items-center text-gray-600 dark:text-gray-400">
                    <span className="font-medium">Versuche:</span>
                    <span className="ml-2">{mail.retryCount} / {mail.maxRetries}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Error Info */}
            {mail.lastError && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Letzter Fehler</h3>
                <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
                  <p className="text-red-600 dark:text-red-400 font-mono text-sm">
                    {mail.lastError}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Set to Failed Modal */}
      <Modal
        isOpen={showFailModal}
        onClose={() => setShowFailModal(false)}
        title="E-Mail als fehlgeschlagen markieren"
        icon={<FontAwesomeIcon icon={faTimesCircle} className="w-5 h-5 text-red-600" />}
        closeDisabled={actionLoading}
      >
        <div className="p-6">
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Bitte geben Sie einen Grund an, warum diese E-Mail als fehlgeschlagen markiert werden soll:
          </p>
          <textarea
            value={failReason}
            onChange={(e) => setFailReason(e.target.value)}
            className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            rows={3}
            placeholder="Grund für das Fehlschlagen..."
          />
          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={() => setShowFailModal(false)}
              disabled={actionLoading}
              className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors cursor-pointer"
            >
              Abbrechen
            </button>
            <button
              onClick={handleSetToFailed}
              disabled={actionLoading || !failReason.trim()}
              className="bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white px-4 py-2 rounded-lg transition-colors cursor-pointer"
            >
              {actionLoading ? (
                <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
              ) : (
                'Als fehlgeschlagen markieren'
              )}
            </button>
          </div>
        </div>
      </Modal>

    </>
  );
}
